<template>
  <rh-etapa-card
    titulo="Escalas de Trabalho"
    subtitulo="Jornada semanal e vigência. As escalas são salvas na hora, sem depender do botão Salvar."
    icone="mdi-calendar-clock"
  >
    <template #acoes>
      <v-btn color="primary" small outlined class="rounded-lg" @click="abrirNovaEscala">
        <v-icon left>mdi-plus</v-icon> Adicionar Escala
      </v-btn>
    </template>

    <v-data-table
      :headers="escalasHeaders"
      :items="escalas"
      :loading="loadingEscalas"
      class="rh-tabela elevation-0"
      no-data-text="Nenhuma escala cadastrada para este colaborador."
      hide-default-footer
      disable-pagination
    >
      <template v-slot:item.ativa="{ item }">
        <v-chip :color="item.ativa ? 'green' : 'grey'" dark small>
          {{ item.ativa ? "Ativa" : "Inativa" }}
        </v-chip>
      </template>
      <template v-slot:item.vigencia_inicio="{ item }">
        {{ formatarDataBR(item.vigencia_inicio) }}
      </template>
      <template v-slot:item.vigencia_fim="{ item }">
        {{ item.vigencia_fim ? formatarDataBR(item.vigencia_fim) : "Sem fim" }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn icon small class="mr-2" color="primary" @click="editarEscala(item)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="deletarEscala(item)">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialogEscala" max-width="900px" persistent>
      <v-card class="rounded-lg">
        <v-card-title
          class="primary white--text py-3 px-4 d-flex align-center justify-space-between mb-2"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-calendar-clock</v-icon>
            <span class="text-h6 font-weight-bold">
              {{ editandoEscalaId ? "Editar Escala de Trabalho" : "Nova Escala de Trabalho" }}
            </span>
          </div>
          <v-btn icon x-small color="white" @click="dialogEscala = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-2">
          <ValidationObserver ref="obsEscala" v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(salvarEscala)">
              <v-container>
                <v-row v-if="!editandoEscalaId" class="mb-2">
                  <v-col cols="12">
                    <v-card outlined class="rh-modelo pa-3 rounded-lg">
                      <div
                        class="subtitle-2 font-weight-bold primary--text mb-1 d-flex align-center"
                      >
                        <v-icon small color="primary" class="mr-1">mdi-content-copy</v-icon>
                        Copiar de uma Escala Existente (Opcional)
                      </div>
                      <div class="caption grey--text mb-2">
                        Selecione um modelo de escala já criado no sistema para carregar
                        automaticamente os horários dos dias da semana.
                      </div>
                      <v-autocomplete
                        v-model="escalaModeloSelecionada"
                        :items="escalasModelosOptions"
                        item-text="label"
                        return-object
                        label="Selecione uma escala criada para carregar horários"
                        placeholder="Pesquise por nome da escala ou colaborador..."
                        outlined
                        dense
                        hide-details
                        clearable
                        :loading="loadingModelos"
                        @change="aplicarEscalaModelo"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title class="font-weight-bold primary--text">
                              {{ item.nome }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="caption grey--text">
                              Colaborador: {{ item.user ? item.user.name : "Sistema" }} | Horários:
                              {{ item.resumoHorarios }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="8">
                    <ValidationProvider name="Nome da Escala" rules="required" v-slot="{ errors }">
                      <e-label>Nome da Escala</e-label>
                      <v-text-field
                        v-model="escalaForm.nome"
                        :error-messages="errors"
                        dense
                        solo
                        placeholder="Ex: ESCALA CAJUTEC, ESCALA ADMINISTRATIVO..."
                      />
                    </ValidationProvider>
                  </v-col>

                  <v-col cols="12" md="4">
                    <e-label>Situação</e-label>
                    <v-switch v-model="escalaForm.ativa" label="Ativa" dense class="mt-1" />
                  </v-col>

                  <v-col cols="12" md="6">
                    <ValidationProvider
                      name="Início da Vigência"
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <e-label>Início da Vigência</e-label>
                      <e-date-customize v-model="escalaForm.vigencia_inicio" />
                      <div class="red--text text-caption" v-if="errors[0]">{{ errors[0] }}</div>
                    </ValidationProvider>
                  </v-col>

                  <v-col cols="12" md="6">
                    <e-label>Fim da Vigência (Opcional)</e-label>
                    <e-date-customize v-model="escalaForm.vigencia_fim" />
                  </v-col>

                  <v-col cols="12">
                    <e-label>Observações (Opcional)</e-label>
                    <v-textarea v-model="escalaForm.observacoes" dense solo rows="2" />
                  </v-col>
                </v-row>

                <h3 class="my-4 font-weight-bold">JORNADA SEMANAL</h3>
                <v-divider class="mb-4" />

                <v-row
                  v-for="(h, idx) in escalaForm.horarios"
                  :key="'horario-' + idx"
                  class="align-center py-2"
                  style="border-bottom: 1px solid var(--border-soft)"
                >
                  <v-col cols="12" md="2">
                    <div class="font-weight-bold">{{ h.nome }}</div>
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-switch v-model="h.trabalha" label="Trabalha" dense hide-details />
                  </v-col>

                  <template v-if="h.trabalha">
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="h.hora_entrada"
                        v-mask="'##:##'"
                        label="Entrada"
                        placeholder="08:00"
                        dense
                        solo
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="h.inicio_intervalo"
                        v-mask="'##:##'"
                        label="Início Int."
                        placeholder="12:00"
                        dense
                        solo
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="h.fim_intervalo"
                        v-mask="'##:##'"
                        label="Fim Int."
                        placeholder="13:00"
                        dense
                        solo
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="h.hora_saida"
                        v-mask="'##:##'"
                        label="Saída"
                        placeholder="17:00"
                        dense
                        solo
                        hide-details
                      />
                    </v-col>
                  </template>

                  <template v-else>
                    <v-col cols="12" md="8">
                      <span class="grey--text font-italic text-caption">Folga / Não trabalha</span>
                    </v-col>
                  </template>
                </v-row>
              </v-container>

              <v-card-actions class="mt-4">
                <v-spacer />
                <v-btn color="secondary" text @click="dialogEscala = false">Cancelar</v-btn>
                <v-btn color="primary" type="submit" :loading="salvandoEscala">Salvar</v-btn>
              </v-card-actions>
            </form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>
  </rh-etapa-card>
</template>

<script>
import RhEtapaCard from "../components/RhEtapaCard.vue";
import { resumoDiasTrabalhados } from "../config/rhConfig";

const DIAS_NOMES = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

function hora(valor) {
  return valor ? String(valor).substring(0, 5) : "";
}

function horarioPadrao(diaSemana) {
  const util = diaSemana >= 1 && diaSemana <= 5;
  return {
    dia_semana: diaSemana,
    nome: DIAS_NOMES[diaSemana],
    trabalha: util,
    hora_entrada: util ? "08:00" : "",
    inicio_intervalo: util ? "12:00" : "",
    fim_intervalo: util ? "13:00" : "",
    hora_saida: util ? "17:00" : "",
    tolerancia_atraso: 10,
  };
}

function horarioDoBanco(diaSemana, dbH) {
  return {
    dia_semana: diaSemana,
    nome: DIAS_NOMES[diaSemana],
    trabalha: !!dbH.trabalha,
    hora_entrada: hora(dbH.hora_entrada),
    inicio_intervalo: hora(dbH.inicio_intervalo),
    fim_intervalo: hora(dbH.fim_intervalo),
    hora_saida: hora(dbH.hora_saida),
    tolerancia_atraso: dbH.tolerancia_atraso || 10,
  };
}

export default {
  name: "EtapaEscalas",
  components: { RhEtapaCard },
  data() {
    return {
      escalas: [],
      loadingEscalas: false,
      dialogEscala: false,
      salvandoEscala: false,
      editandoEscalaId: null,
      escalasModelos: [],
      escalaModeloSelecionada: null,
      loadingModelos: false,
      escalaForm: {
        nome: "",
        ativa: true,
        vigencia_inicio: "",
        vigencia_fim: "",
        observacoes: "",
        horarios: [],
      },
      escalasHeaders: [
        { text: "Nome da Escala", value: "nome", sortable: true },
        { text: "Situação", value: "ativa", sortable: true },
        { text: "Início Vigência", value: "vigencia_inicio", sortable: true },
        { text: "Fim Vigência", value: "vigencia_fim", sortable: true },
        { text: "Ações", value: "actions", sortable: false, align: "right" },
      ],
    };
  },
  computed: {
    usuarioId() {
      return this.$route.params.usuario_id;
    },
    escalasModelosOptions() {
      return this.escalasModelos.map((m) => {
        const resumo = resumoDiasTrabalhados(m);
        const userName = m.user ? m.user.name : "Sistema";
        return {
          ...m,
          label: `${m.nome} (${userName}) — [${resumo}]`,
          resumoHorarios: resumo,
        };
      });
    },
  },
  mounted() {
    this.loadEscalas();
  },
  methods: {
    async loadEscalas() {
      if (!this.usuarioId) {
        return;
      }
      this.loadingEscalas = true;
      try {
        this.escalas = await this.$services.recursosHumanosService.getEscalas(this.usuarioId);
        this.$emit("carregadas", this.escalas);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loadingEscalas = false;
      }
    },
    async carregarEscalasModelos() {
      try {
        this.loadingModelos = true;
        this.escalasModelos = await this.$services.recursosHumanosService.getEscalasModelos();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loadingModelos = false;
      }
    },
    async abrirNovaEscala() {
      this.editandoEscalaId = null;
      this.escalaModeloSelecionada = null;
      await this.carregarEscalasModelos();
      this.escalaForm = {
        nome: "",
        ativa: true,
        vigencia_inicio: new Date().toISOString().substr(0, 10),
        vigencia_fim: "",
        observacoes: "",
        horarios: DIAS_NOMES.map((nome, dia) => horarioPadrao(dia)),
      };
      this.dialogEscala = true;
    },
    aplicarEscalaModelo(modelo) {
      if (!modelo) {
        return;
      }
      this.escalaForm.nome = modelo.nome;
      DIAS_NOMES.forEach((nome, dia) => {
        const dbH = (modelo.horarios || []).find((h) => h.dia_semana === dia);
        if (dbH) {
          this.$set(this.escalaForm.horarios, dia, horarioDoBanco(dia, dbH));
        }
      });
      this.$toast.success(`Horários copiados da escala "${modelo.nome}" com sucesso!`);
    },
    async salvarEscala() {
      this.salvandoEscala = true;
      try {
        if (this.editandoEscalaId) {
          await this.$services.recursosHumanosService.atualizarEscala(
            this.usuarioId,
            this.editandoEscalaId,
            this.escalaForm
          );
          this.$toast.success("Escala atualizada com sucesso");
        } else {
          await this.$services.recursosHumanosService.salvarEscala(this.usuarioId, this.escalaForm);
          this.$toast.success("Escala cadastrada com sucesso");
        }
        this.dialogEscala = false;
        await this.loadEscalas();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvandoEscala = false;
      }
    },
    editarEscala(item) {
      this.editandoEscalaId = item.id;
      const copia = JSON.parse(JSON.stringify(item));
      const horarios = DIAS_NOMES.map((nome, dia) => {
        const dbH = (copia.horarios || []).find((h) => h.dia_semana === dia);
        return dbH ? horarioDoBanco(dia, dbH) : { ...horarioPadrao(dia), trabalha: false };
      });

      this.escalaForm = {
        nome: copia.nome,
        ativa: !!copia.ativa,
        vigencia_inicio: copia.vigencia_inicio ? copia.vigencia_inicio.substr(0, 10) : "",
        vigencia_fim: copia.vigencia_fim ? copia.vigencia_fim.substr(0, 10) : "",
        observacoes: copia.observacoes || "",
        horarios,
      };
      this.dialogEscala = true;
    },
    async deletarEscala(item) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Deseja realmente excluir a escala "${item.nome}"?`)) {
        return;
      }
      this.loadingEscalas = true;
      try {
        await this.$services.recursosHumanosService.deletarEscala(this.usuarioId, item.id);
        this.$toast.success("Escala excluída com sucesso");
        await this.loadEscalas();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loadingEscalas = false;
      }
    },
    formatarDataBR(val) {
      if (!val) {
        return "";
      }
      const date = new Date(val);
      if (Number.isNaN(date.getTime())) {
        return val;
      }
      const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return utcDate.toLocaleDateString("pt-BR");
    },
  },
};
</script>

<style scoped>
.rh-tabela {
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}

.rh-modelo {
  background: var(--surface-2) !important;
}
</style>
